import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { Form, Formik } from 'formik';

import { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import useCheckPermission from '../../hooks/useCheckPermission';

import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';

import ColorPickerField from '../../components/ColorPickerField';

import mobileApplicationReleaseProfileService from '../../services/mobileApplicationReleaseProfileService';

import tenantService from '../tenants/tenantService';

import tenantGroupService from '../tenantGroups/tenantGroupService';

import BuildApiKeyRevealDialog from './BuildApiKeyRevealDialog';

import ReleaseProfileImageField from './ReleaseProfileImageField';



const INFO_BANNER =

  'Ezek a beállítások csak új alkalmazás build után lépnek életbe (Play Store / TestFlight). A futó app kinézetét az Applikáció témák menüpontban állíthatod; az app újraindításakor frissül.';



const ASSET_GUIDE = [

  ['Launcher / App ikon', 'PNG, 1024×1024 px, négyzetes', 'Pipeline méretezi Android + iOS ikonokhoz.'],

  ['Biztonságos zóna', 'Fontos grafika ~66% középen', 'Adaptive icon levághatja a sarkokat.'],

  ['Átlátszóság', 'Kerüld az alpha csatornát', 'iOS elutasíthatja; remove_alpha_ios fehér háttér.'],

  ['Android splash foreground', 'PNG, min. 512×512', 'Egyszerű grafika, ne teljes képernyős fotó.'],

  ['Splash háttérszín', 'Hex #RRGGBB', 'Natív splash szín alapú; háttérkép runtime téma.'],

];

/** Sample city-themed copy shown as placeholders; create defaults use the same strings. */
export const AGGREGATOR_COPY_FIELDS = [
  {
    field: 'tenantPickerTitle',
    env: 'TENANT_PICKER_TITLE',
    label: 'Választó cím (első indítás)',
    sample: 'Város választása',
  },
  {
    field: 'tenantSwitcherTitle',
    env: 'TENANT_SWITCHER_TITLE',
    label: 'Váltó cím / dialógus',
    sample: 'Város váltása',
  },
  {
    field: 'tenantSwitcherButtonText',
    env: 'TENANT_SWITCHER_BUTTON_TEXT',
    label: 'Drawer gomb',
    sample: 'Város váltása',
  },
  {
    field: 'tenantDrawerFallbackLabel',
    env: 'TENANT_DRAWER_FALLBACK_LABEL',
    label: 'Drawer címke (név nélkül)',
    sample: 'Város',
  },
  {
    field: 'tenantSwitchConfirmMessage',
    env: 'TENANT_SWITCH_CONFIRM_MESSAGE',
    label: 'Váltás megerősítés (2. mondat)',
    sample: 'A tartalom az új városhoz frissül.',
  },
  {
    field: 'tenantPickerEmptyMessage',
    env: 'TENANT_PICKER_EMPTY_MESSAGE',
    label: 'Üres lista',
    sample: 'Nincs elérhető város.',
  },
  {
    field: 'tenantPickerEmptyInGroupMessage',
    env: 'TENANT_PICKER_EMPTY_IN_GROUP_MESSAGE',
    label: 'Üres csoport',
    sample: 'Nincs elérhető város ebben a csoportban.',
  },
  {
    field: 'tenantPickerLoadErrorMessage',
    env: 'TENANT_PICKER_LOAD_ERROR_MESSAGE',
    label: 'Betöltési hiba',
    sample: 'Nem sikerült betölteni a városokat. Ellenőrizd az internetkapcsolatot.',
  },
  {
    field: 'tenantPickerUnavailableMessage',
    env: 'TENANT_PICKER_UNAVAILABLE_MESSAGE',
    label: 'HTTP hiba prefix',
    sample: 'A városlista nem érhető el',
  },
  {
    field: 'tenantSelectErrorMessage',
    env: 'TENANT_SELECT_ERROR_MESSAGE',
    label: 'Kiválasztás hiba',
    sample: 'Nem sikerült a város kiválasztása.',
  },
] as const;

export const AGGREGATOR_COPY_DEFAULTS = Object.fromEntries(
  AGGREGATOR_COPY_FIELDS.map((f) => [f.field, f.sample])
) as Record<(typeof AGGREGATOR_COPY_FIELDS)[number]['field'], string>;



const BUILD_FILE_KIND = {

  googleServicesJson: 0,

  googleServiceInfoPlist: 1,

  launcherIcon: 2,

  splashForegroundIcon: 3,

  appIcon: 4,

} as const;

const DEPLOYMENT_MODE = {
  singleTenant: 0,
  aggregator: 1,
} as const;

const DEPLOYMENT_MODE_OPTIONS = [
  { value: DEPLOYMENT_MODE.singleTenant, label: 'Single (egy bérlő)' },
  { value: DEPLOYMENT_MODE.aggregator, label: 'Aggregátor (bérlőcsoport)' },
];



async function fileToBase64(file: File): Promise<string> {

  const buffer = await file.arrayBuffer();

  let binary = '';

  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < bytes.length; i++) {

    binary += String.fromCharCode(bytes[i]);

  }

  return btoa(binary);

}



const MobileApplicationReleaseProfileForm = (props: any) => {

  const {

    entity,

    onSubmit,

    errors,

    handleCancel,

    readonly = false,

    onApiKeyRevealed,

  } = props;



  const { canPlatform } = useCheckPermission();
  const canManage = canPlatform(['ManageBuildProfiles']);

  const { enqueueSnackbar } = useSnackbar();

  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();

  const [tab, setTab] = useState(0);

  const [tenants, setTenants] = useState<any[]>([]);

  const [tenantGroups, setTenantGroups] = useState<any[]>([]);

  const isSmallScreen = useMediaQuery((theme: any) =>

    theme.breakpoints.down('sm')

  );

  useEffect(() => {
    tenantService.list().then((response) => {
      if (!response.hasError) {
        setTenants(response.records || []);
      }
    });
    tenantGroupService.list().then((response) => {
      if (!response.hasError) {
        setTenantGroups(response.records || []);
      }
    });
  }, []);



  const uploadFirebaseFile = async (

    file: File,

    kind: number,

    setFieldValue: (field: string, value: any) => void,

    fieldId: string,

    fieldObj: string

  ) => {

    const contentBase64 = await fileToBase64(file);

    const response = await mobileApplicationReleaseProfileService.uploadBuildConfigFile({

      kind,

      fileName: file.name,

      contentBase64,

    });

    if (response.hasError) {

      enqueueSnackbar(response.errorMessages.join(','), { variant: 'error' });

      return;

    }

    setFieldValue(fieldId, response.result.id);

    setFieldValue(fieldObj, response.result);

    enqueueSnackbar('Firebase fájl feltöltve.', { variant: 'success' });

  };



  const handleRotateKey = (profileId: number) => {

    setParams({

      open: true,

      name:

        'A régi kulcs azonnal érvénytelen lesz. A teljes új kulcs csak most látható. Frissítsd az ADO RELEASE_PROFILE_API_KEY secretet!',

      onConfirm: async () => {

        const response =

          await mobileApplicationReleaseProfileService.rotateBuildApiKey(profileId);

        if (response.hasError) {

          enqueueSnackbar(response.errorMessages.join(','), { variant: 'error' });

        } else if (onApiKeyRevealed) {

          onApiKeyRevealed(response.result.plainTextBuildApiKey);

        }

      },

    });

  };



  if (!entity) {
    return null;
  }

  const resolveDeploymentMode = (values: any) =>
    Number(values.deploymentMode ?? values.DeploymentMode ?? DEPLOYMENT_MODE.singleTenant);

  return (

    <Formik

      initialValues={{
        ...entity,
        deploymentMode:
          entity.deploymentMode ??
          entity.DeploymentMode ??
          DEPLOYMENT_MODE.singleTenant,
        tenantGroupId: entity.tenantGroupId ?? entity.TenantGroupId ?? null,
      }}

      enableReinitialize

      onSubmit={(values, { setSubmitting }) => {
        const mode = resolveDeploymentMode(values);
        onSubmit(
          {
            ...values,
            deploymentMode: mode,
            tenantId:
              mode === DEPLOYMENT_MODE.singleTenant
                ? values.tenantId ?? values.TenantId ?? null
                : null,
            tenantGroupId:
              mode === DEPLOYMENT_MODE.aggregator
                ? values.tenantGroupId ?? values.TenantGroupId ?? null
                : null,
          },
          setSubmitting
        );
      }}

    >

      {({ isSubmitting, values, setFieldValue }) => {

        const mode = resolveDeploymentMode(values);

        return (

        <Form>

          <Paper>

            <Grid container spacing={2} sx={{ p: 5 }}>

              <Grid size={12}>

                <h2>

                  Kiadási profil{' '}

                  {(values?.id ?? 0) > 0 ? 'szerkesztése' : 'létrehozása'}

                </h2>

              </Grid>

              <Grid size={12}>

                <Alert severity="info">{INFO_BANNER}</Alert>

              </Grid>

              {values.id > 0 && (

                <Grid size={12}>

                  <Paper variant="outlined" sx={{ p: 2 }}>

                    <Typography variant="subtitle2" gutterBottom>

                      Pipeline: CMS_BUILD_PROFILE_ID

                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

                      <TextField

                        fullWidth

                        size="small"

                        value={values.publicId || ''}

                        slotProps={{ input: { readOnly: true } }}

                        label="PublicId (GUID)"

                      />

                      <Button

                        variant="outlined"

                        onClick={async () => {

                          await navigator.clipboard.writeText(values.publicId);

                          enqueueSnackbar('GUID másolva.', { variant: 'success' });

                        }}

                      >

                        Másolás

                      </Button>

                    </Box>

                  </Paper>

                </Grid>

              )}

              {values.id > 0 && (

                <Grid size={12}>

                  <Paper variant="outlined" sx={{ p: 2 }}>

                    <Typography variant="subtitle2" gutterBottom>

                      CI API kulcs

                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>

                      {values.buildApiKeyPrefix

                        ? `${values.buildApiKeyPrefix}••••••••`

                        : '—'}

                      {values.buildApiKeyRotatedAt && (

                        <> — utolsó rotálás: {values.buildApiKeyRotatedAt}</>

                      )}

                    </Typography>

                    <Alert severity="warning" sx={{ mb: 1 }}>

                      A kulcsot nem tároljuk visszafejthetően. Ha elveszett,

                      generálj újat.

                    </Alert>

                    {canManage && (

                      <Button

                        variant="contained"

                        color="warning"

                        onClick={() => handleRotateKey(values.id)}

                      >

                        Új API kulcs generálása

                      </Button>

                    )}

                  </Paper>

                </Grid>

              )}

              <Grid size={12}>

                <Box sx={{ color: 'red' }}>

                  {errors.map((error: string, i: number) => (

                    <li key={i}>{error}</li>

                  ))}

                </Box>

              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>

                <TextField

                  fullWidth

                  required

                  label="Profil neve (admin)"

                  name="name"

                  value={values.name || ''}

                  disabled={readonly}

                  onChange={(e) => setFieldValue('name', e.target.value)}

                />

              </Grid>

              <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      fullWidth
                      required
                      label="Kitelepítési mód"
                      value={mode}
                      disabled={readonly}
                      onChange={(e) => {
                        const nextMode = Number(e.target.value);
                        setFieldValue('deploymentMode', nextMode);
                        if (nextMode === DEPLOYMENT_MODE.singleTenant) {
                          setFieldValue('tenantGroupId', null);
                        } else {
                          setFieldValue('tenantId', null);
                        }
                      }}
                      helperText="Single: egy bérlő. Aggregátor: bérlőcsoport városválasztóval."
                    >
                      {DEPLOYMENT_MODE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {mode === DEPLOYMENT_MODE.singleTenant && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={tenants}
                        value={
                          tenants.find(
                            (t) =>
                              (t.id ?? t.Id) === (values.tenantId ?? values.TenantId)
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          option?.name ?? option?.Name ?? ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          (option?.id ?? option?.Id) === (value?.id ?? value?.Id)
                        }
                        disabled={readonly}
                        onChange={(_, value) =>
                          setFieldValue('tenantId', value ? value.id ?? value.Id : null)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Kapcsolt bérlő"
                            helperText="Single módban kötelező."
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {mode === DEPLOYMENT_MODE.aggregator && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={tenantGroups}
                        value={
                          tenantGroups.find(
                            (g) =>
                              (g.id ?? g.Id) ===
                              (values.tenantGroupId ?? values.TenantGroupId)
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          option?.name ?? option?.Name ?? ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          (option?.id ?? option?.Id) === (value?.id ?? value?.Id)
                        }
                        disabled={readonly}
                        onChange={(_, value) =>
                          setFieldValue(
                            'tenantGroupId',
                            value ? value.id ?? value.Id : null
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Bérlőcsoport"
                            helperText="Aggregátor módban kötelező; legalább egy aktív tag kell."
                          />
                        )}
                      />
                    </Grid>
                  )}
                </>

              <Grid size={12}>

                <Tabs value={tab} onChange={(_, v) => setTab(v)}>

                  <Tab label="Közös" />

                  <Tab label="Android" />

                  <Tab label="iOS" />

                  <Tab label="Aggregátor szövegek" />

                  <Tab label="Asset útmutató" />

                </Tabs>

              </Grid>



              {tab === 0 && (

                <>

                  <Grid size={12}>

                    <Alert severity="info">{INFO_BANNER}</Alert>

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="App cím (APP_TITLE)"

                      value={values.appTitle || ''}

                      disabled={readonly}

                      onChange={(e) => setFieldValue('appTitle', e.target.value)}

                    />

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="Menü lábléc szöveg (DRAWER_FOOTER_TEXT)"

                      helperText="Oldalsó menü alján megjelenő szöveg; üres = nem jelenik meg"

                      value={values.drawerFooterText || ''}

                      disabled={readonly}

                      onChange={(e) =>

                        setFieldValue('drawerFooterText', e.target.value)

                      }

                    />

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="Adatvédelmi URL"

                      type="url"

                      value={values.privacyPolicyUrl || ''}

                      disabled={readonly}

                      error={
                        !!values.privacyPolicyUrl &&
                        !/^https?:\/\/.+/i.test(String(values.privacyPolicyUrl))
                      }

                      helperText={
                        values.privacyPolicyUrl &&
                        !/^https?:\/\/.+/i.test(String(values.privacyPolicyUrl))
                          ? 'Érvényes http(s) URL kell'
                          : 'Teljes URL (https://…)'
                      }

                      onChange={(e) =>

                        setFieldValue('privacyPolicyUrl', e.target.value)

                      }

                    />

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <Autocomplete

                      freeSolo

                      options={['https', 'http', 'myapp', 'cloudscape']}

                      value={values.deepLinkScheme || ''}

                      disabled={readonly}

                      onChange={(_e, v) =>
                        setFieldValue(
                          'deepLinkScheme',
                          typeof v === 'string' ? v : v ?? ''
                        )
                      }

                      onInputChange={(_e, v) =>
                        setFieldValue('deepLinkScheme', v)
                      }

                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Deep link scheme"
                          helperText="Közös scheme (pl. hermesz)"
                        />
                      )}

                    />

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="Deep link host"

                      value={values.deepLinkHost || ''}

                      disabled={readonly}

                      onChange={(e) =>

                        setFieldValue('deepLinkHost', e.target.value)

                      }

                      helperText="Tenant link: scheme://host/tenant/{slug} — ne legyen benne „parking”"

                      placeholder="app.hermesz.eu"

                    />

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Térkép AppBar (MAP_TITLE)"
                      placeholder="Városi térkép"
                      value={values.mapTitle || ''}
                      disabled={readonly}
                      onChange={(e) => setFieldValue('mapTitle', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Térkép nav címke (MAP_NAV_LABEL)"
                      placeholder="TÉRKÉP"
                      value={values.mapNavLabel || ''}
                      disabled={readonly}
                      onChange={(e) =>
                        setFieldValue('mapNavLabel', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Vezetés AppBar (OFFICIALS_TITLE)"
                      placeholder="Vezetés"
                      helperText="Officials lista címe a mobil AppBar-ban"
                      value={values.officialsTitle || ''}
                      disabled={readonly}
                      onChange={(e) =>
                        setFieldValue('officialsTitle', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid size={12}>

                    <FormControlLabel

                      control={

                        <Switch

                          checked={!!values.enableParking}

                          disabled={readonly}

                          onChange={(e) =>

                            setFieldValue('enableParking', e.target.checked)

                          }

                        />

                      }

                      label="Parkolás engedélyezése"

                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.enableMap !== false}
                          disabled={readonly}
                          onChange={(e) =>
                            setFieldValue('enableMap', e.target.checked)
                          }
                        />
                      }
                      label="Térkép engedélyezése (ENABLE_MAP)"
                    />

                    <FormControlLabel

                      control={

                        <Switch

                          checked={values.enableHtmlApplets !== false}

                          disabled={readonly}

                          onChange={(e) =>

                            setFieldValue('enableHtmlApplets', e.target.checked)

                          }

                        />

                      }

                      label="HTML appletok"

                    />

                    <FormControlLabel

                      control={

                        <Switch

                          checked={values.enableAnalytics !== false}

                          disabled={readonly}

                          onChange={(e) =>

                            setFieldValue('enableAnalytics', e.target.checked)

                          }

                        />

                      }

                      label="Analitika"

                    />

                  </Grid>

                  <Grid size={12}>

                    <TextField

                      fullWidth

                      multiline

                      minRows={2}

                      label="Belső megjegyzés"

                      value={values.notes || ''}

                      disabled={readonly}

                      onChange={(e) => setFieldValue('notes', e.target.value)}

                    />

                  </Grid>

                </>

              )}



              {tab === 1 && (

                <>

                  <Grid size={12}>

                    <Alert severity="info">{INFO_BANNER}</Alert>

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="Application label"

                      helperText="Üres → App cím"

                      value={values.applicationLabel || ''}

                      disabled={readonly}

                      onChange={(e) =>

                        setFieldValue('applicationLabel', e.target.value)

                      }

                    />

                  </Grid>

                  <Grid size={12}>

                    <ColorPickerField

                      label="Adaptive icon háttér"

                      value={values.adaptiveIconBackgroundColor}

                      readonly={readonly}

                      onChange={(hex) =>

                        setFieldValue('adaptiveIconBackgroundColor', hex)

                      }

                    />

                  </Grid>

                  <Grid size={12}>

                    <ColorPickerField

                      label="Splash háttér"

                      value={values.splashBackgroundColor}

                      readonly={readonly}

                      onChange={(hex) =>

                        setFieldValue('splashBackgroundColor', hex)

                      }

                    />

                  </Grid>

                  <Grid size={12}>

                    <ReleaseProfileImageField

                      label="Launcher ikon"

                      kind={BUILD_FILE_KIND.launcherIcon}

                      value={values.launcherIcon}

                      readonly={readonly}

                      onChange={(id, file) => {

                        setFieldValue('launcherIconId', id);

                        setFieldValue('launcherIcon', file);

                      }}

                    />

                  </Grid>

                  <Grid size={12}>

                    <ReleaseProfileImageField

                      label="Splash foreground ikon"

                      kind={BUILD_FILE_KIND.splashForegroundIcon}

                      value={values.splashForegroundIcon}

                      readonly={readonly}

                      onChange={(id, file) => {

                        setFieldValue('splashForegroundIconId', id);

                        setFieldValue('splashForegroundIcon', file);

                      }}

                    />

                  </Grid>

                  <Grid size={12}>

                    <Typography variant="subtitle1">

                      google-services.json

                    </Typography>

                    {values.googleServicesJsonFile?.fileName && (

                      <Typography variant="body2">

                        Feltöltve: {values.googleServicesJsonFile.fileName} (

                        {values.googleServicesJsonFile.extractedAppId})

                      </Typography>

                    )}

                    {!readonly && (

                      <Button variant="outlined" component="label" sx={{ mt: 1 }}>

                        Fájl feltöltése

                        <input

                          type="file"

                          accept=".json,application/json"

                          hidden

                          onChange={async (e) => {

                            const file = e.target.files?.[0];

                            if (file) {

                              await uploadFirebaseFile(

                                file,

                                BUILD_FILE_KIND.googleServicesJson,

                                setFieldValue,

                                'googleServicesJsonFileId',

                                'googleServicesJsonFile'

                              );

                            }

                            e.target.value = '';

                          }}

                        />

                      </Button>

                    )}

                  </Grid>

                </>

              )}



              {tab === 2 && (

                <>

                  <Grid size={12}>

                    <Alert severity="info">{INFO_BANNER}</Alert>

                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>

                    <TextField

                      fullWidth

                      label="Display name (CFBundleDisplayName)"

                      helperText="Üres → App cím"

                      value={values.displayName || ''}

                      disabled={readonly}

                      onChange={(e) =>

                        setFieldValue('displayName', e.target.value)

                      }

                    />

                  </Grid>

                  <Grid size={12}>

                    <ColorPickerField

                      label="Launch screen háttér"

                      value={values.launchScreenBackgroundColor}

                      readonly={readonly}

                      onChange={(hex) =>

                        setFieldValue('launchScreenBackgroundColor', hex)

                      }

                    />

                  </Grid>

                  <Grid size={12}>

                    <ReleaseProfileImageField

                      label="App ikon (1024×1024)"

                      kind={BUILD_FILE_KIND.appIcon}

                      value={values.appIcon}

                      readonly={readonly}

                      onChange={(id, file) => {

                        setFieldValue('appIconId', id);

                        setFieldValue('appIcon', file);

                      }}

                    />

                  </Grid>

                  <Grid size={12}>

                    <Typography variant="subtitle1">

                      GoogleService-Info.plist

                    </Typography>

                    {values.googleServiceInfoPlistFile?.fileName && (

                      <Typography variant="body2">

                        Feltöltve: {values.googleServiceInfoPlistFile.fileName} (

                        {values.googleServiceInfoPlistFile.extractedAppId})

                      </Typography>

                    )}

                    {!readonly && (

                      <Button variant="outlined" component="label" sx={{ mt: 1 }}>

                        Fájl feltöltése

                        <input

                          type="file"

                          accept=".plist,application/xml,text/xml"

                          hidden

                          onChange={async (e) => {

                            const file = e.target.files?.[0];

                            if (file) {

                              await uploadFirebaseFile(

                                file,

                                BUILD_FILE_KIND.googleServiceInfoPlist,

                                setFieldValue,

                                'googleServiceInfoPlistFileId',

                                'googleServiceInfoPlistFile'

                              );

                            }

                            e.target.value = '';

                          }}

                        />

                      </Button>

                    )}

                  </Grid>

                </>

              )}



              {tab === 3 && (
                <>
                  <Grid size={12}>
                    <Alert severity="info">
                      Csak Aggregátor buildben használt UI szövegek (város / cég / terület
                      tematika). Üres mező = az app beépített defaultja. A mintaszöveg
                      város-tematikájú példát mutat.
                    </Alert>
                  </Grid>
                  {mode !== DEPLOYMENT_MODE.aggregator && (
                    <Grid size={12}>
                      <Alert severity="warning">
                        A profil jelenleg SingleTenant módban van — ezek a mezők csak
                        Aggregátor módban kerülnek a buildbe.
                      </Alert>
                    </Grid>
                  )}
                  {AGGREGATOR_COPY_FIELDS.map(({ field, env, label, sample }) => (
                    <Grid key={field} size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={`${label} (${env})`}
                        placeholder={sample}
                        helperText={`Minta: ${sample}`}
                        value={values[field] || ''}
                        disabled={readonly || mode !== DEPLOYMENT_MODE.aggregator}
                        onChange={(e) => setFieldValue(field, e.target.value)}
                      />
                    </Grid>
                  ))}
                </>
              )}

              {tab === 4 && (

                <Grid size={12}>

                  <Typography variant="h6" gutterBottom>

                    Asset útmutató

                  </Typography>

                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>

                    <thead>

                      <tr>

                        <th align="left">Asset</th>

                        <th align="left">Spec</th>

                        <th align="left">Megjegyzés</th>

                      </tr>

                    </thead>

                    <tbody>

                      {ASSET_GUIDE.map(([a, b, c]) => (

                        <tr key={a}>

                          <td>{a}</td>

                          <td>{b}</td>

                          <td>{c}</td>

                        </tr>

                      ))}

                    </tbody>

                  </Box>

                </Grid>

              )}



              <Grid container sx={{ pt: 3 }}>

                {!readonly && (

                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>

                    <Button

                      type="submit"

                      variant="contained"

                      fullWidth={isSmallScreen}

                      disabled={isSubmitting}

                    >

                      Mentés

                    </Button>

                  </Grid>

                )}

                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>

                  <Button

                    variant="outlined"

                    color="secondary"

                    fullWidth={isSmallScreen}

                    onClick={handleCancel}

                  >

                    Mégse

                  </Button>

                </Grid>

              </Grid>

            </Grid>

          </Paper>

          <ConfirmDeleteDialog />

        </Form>

        );

      }}

    </Formik>

  );

};



export default MobileApplicationReleaseProfileForm;


